// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { ActionType, IAction, raiseAction } from "actions";
import { IState } from "states/states";
import { IOcppCharger } from "states/ocppstates";

import { ReactComponent as IconLocked } from "static/locked.svg";
import { ReactComponent as IconUnlocked } from "static/unlocked.svg";
import MyFormattedDate from "./FormattedDate";
import MyFormattedTime from "./FormattedTime";

interface IChargerParameterProps {
  chargerId: number | undefined;
  ocppCharger: IOcppCharger | undefined;
  lastOcppMessage: number | undefined;
  selectedConnector: number | undefined;
  canModifyChargers: boolean;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const chargerId = state.router.charger;
  const chargerIndex = state.ocpp.charger.findIndex((curItem) => curItem.fk_charger === chargerId);
  const ocppCharger = chargerIndex >= 0 ? state.ocpp.charger[chargerIndex] : undefined;

  const props: IChargerParameterProps = {
    chargerId,
    ocppCharger,
    lastOcppMessage: state.ocpp.lastOcppMessage[chargerIndex],
    selectedConnector: state.router.connector,
    canModifyChargers: curUser?.accessRights.ocppSettings ?? false,
  };
  return props;
};

interface IChargerParameterDispatch {
  onEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerParameterDispatch = {
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_CHARGER, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  chargerId: {
    id: "Charger index",
  },
});

class ChargerParameterComponent extends React.Component<
  IChargerParameterProps & IChargerParameterDispatch & WrappedComponentProps
> {
  public render() {
    return (
      <div id="chargerparameters">
        <table className="datatable">
          <tbody>
            <tr>
              <th>
                <FormattedMessage id="Vendor" />
              </th>
              <td
                title={`${this.props.intl.formatMessage(messages.chargerId)}: ${
                  this.props.ocppCharger?.fk_charger
                }`}
              >
                {this.props.ocppCharger?.vendor}
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Model" />
              </th>
              <td>{this.props.ocppCharger?.model}</td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Software" />
              </th>
              <td>{this.props.ocppCharger?.firmware}</td>
            </tr>
            {Boolean(this.props.ocppCharger?.serial) && (
              <tr>
                <th>
                  <FormattedMessage id="Serial Number" />
                </th>
                <td>{this.props.ocppCharger?.serial}</td>
              </tr>
            )}
            <tr>
              <th>
                <FormattedMessage id="Last Data" />
              </th>
              <td>
                <MyFormattedDate valueS={this.props.lastOcppMessage} />{" "}
                <MyFormattedTime valueS={this.props.lastOcppMessage} />
              </td>
            </tr>
            <tr>
              <th>
                <FormattedMessage id="Charging authentication" />
              </th>
              <td>
                <span
                  className={`nowrap${this.props.canModifyChargers ? " link" : ""}`}
                  onClick={
                    this.props.canModifyChargers
                      ? () => this.props.onEdit(this.props.chargerId!)
                      : undefined
                  }
                >
                  {this.props.ocppCharger?.restrictAuth ? (
                    <>
                      <IconLocked className="me-1" />
                      <FormattedMessage id="Authorized" />
                    </>
                  ) : (
                    <>
                      <IconUnlocked className="me-1" />
                      <FormattedMessage id="Public" />
                    </>
                  )}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChargerParameterComponent));
